import type { ValidationQueueItem } from "../services/validations";
import type { CostCenter, Profile, TaskRow, ValidationStatus } from "../types";
import { gmrCodeForUdr, lineCenterCodeForTask } from "../utils/validation";

export type DemoPersona = "admin" | "udr" | "gmr" | "line";

export const DEMO_PERSONAS: Record<DemoPersona, { label: string; profile: Profile; center: CostCenter }> = {
  admin: {
    label: "Administrador POI",
    profile: { id: "demo-admin", fullName: "Administrador POI", email: "admin@sis.gob.pe", role: "admin" },
    center: { id: "cc-jefatura", code: "01", name: "JEFATURA" },
  },
  udr: {
    label: "UDR Cajamarca",
    profile: { id: "demo-udr", fullName: "Responsable UDR Cajamarca", email: "udr.cajamarca@sis.gob.pe", role: "user" },
    center: { id: "cc-udr-cajamarca", code: "03.01.01", name: "UDR CAJAMARCA" },
  },
  gmr: {
    label: "GMR Norte",
    profile: { id: "demo-gmr", fullName: "Validador GMR Norte", email: "gmr.norte@sis.gob.pe", role: "user" },
    center: { id: "cc-gmr-norte", code: "03.01", name: "GERENCIA MACRO REGIONAL NORTE" },
  },
  line: {
    label: "Órgano de línea · GREP",
    profile: { id: "demo-grep", fullName: "Validador GREP", email: "grep@sis.gob.pe", role: "user" },
    center: { id: "cc-grep", code: "01.02", name: "GERENCIA DE RIESGO Y EVALUACIÓN DE LAS PRESTACIONES" },
  },
};

export const demoUdrTasks: TaskRow[] = [
  {
    id: "demo-udr-risk", legacyKey: "030101M1.05.05.07", activityCode: "M1.05.05",
    activityName: "Gestión de riesgos de prestaciones", taskCode: "M1.05.05.07",
    taskName: "Ejecutar acciones de auditoría electrónica de prestaciones", unit: "Acción",
    operationalDefinition: "Consiste en la verificación electrónica de los registros de prestaciones de salud financiadas por el SIS para validar la calidad, oportunidad e integralidad de las atenciones brindadas.",
    definitionResponsible: "01.02 - GERENCIA DE RIESGO Y EVALUACIÓN DE LAS PRESTACIONES",
    definitionUpdatedOn: "2026-01-14",
    baselineVersionNumber: 2,
    baseline: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    modified: [null, null, null, null, null, null, null, 2, null, null, null, null],
  },
  {
    id: "demo-udr-support", legacyKey: "030101S1.01.07.01", activityCode: "S1.01.07",
    activityName: "Soporte administrativo de la UDR", taskCode: "S1.01.07.01",
    baselineVersionNumber: 0,
    taskName: "Gestionar requerimientos administrativos de la UDR", unit: "Acción",
    operationalDefinition: "Acciones de soporte para el uso óptimo de los recursos institucionales en la UDR, comprendiendo los sistemas administrativos, imagen institucional y soporte informático.",
    definitionResponsible: "INTERNO",
    definitionUpdatedOn: "2025-11-05",
    baseline: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    modified: [null, null, null, null, null, null, null, null, null, null, 2, null],
  },
];

const LINE_NAMES: Record<string, string> = {
  "01.01": "GERENCIA DEL ASEGURADO",
  "01.02": "GERENCIA DE RIESGO Y EVALUACIÓN DE LAS PRESTACIONES",
  "01.03": "GERENCIA DE NEGOCIOS Y FINANCIAMIENTO",
};

export function createDemoValidationQueue(tasks: TaskRow[], versionNumber: number, taskJustifications: Record<string, string> = {}): ValidationQueueItem[] {
  const center = DEMO_PERSONAS.udr.center;
  const gmrCode = gmrCodeForUdr(center.code) ?? "03.01";
  return tasks.filter((task) => task.modified.some((value) => value !== null)).map((task) => {
    const lineCode = lineCenterCodeForTask(task.taskCode) ?? null;
    return {
      validationId: `demo-validation-${versionNumber}-${task.id}`,
      versionId: `demo-version-${versionNumber}`,
      versionNumber,
      submittedAt: new Date().toISOString(),
      centerCode: center.code,
      centerName: center.name,
      taskId: task.id,
      taskCode: task.taskCode,
      taskName: task.taskName,
      activityCode: task.activityCode,
      activityName: task.activityName,
      unit: task.unit,
      values: task.baseline.map((value, index) => task.modified[index] ?? value),
      gmrCode,
      lineCode,
      justification: taskJustifications[task.id] ?? "Sustento demostrativo de la tarea modificada",
      lineName: lineCode ? LINE_NAMES[lineCode] : null,
      gmrStatus: "pending",
      lineStatus: lineCode ? "waiting" : "not_required",
      gmrComment: null,
      lineComment: null,
      reviewStage: "gmr",
    };
  });
}

export function demoValidationStatus(rows: ValidationQueueItem[]): ValidationStatus {
  if (!rows.length) return "not_submitted";
  if (rows.some((row) => row.gmrStatus === "observed")) return "observed_gmr";
  if (rows.some((row) => row.lineStatus === "observed")) return "observed_line";
  if (rows.some((row) => row.gmrStatus === "pending")) return "pending_gmr";
  if (rows.some((row) => row.lineStatus === "waiting" || row.lineStatus === "pending")) return "pending_line";
  return "accepted";
}

export function demoValidationObservation(rows: ValidationQueueItem[]) {
  const observed = rows.find((row) => row.gmrStatus === "observed" || row.lineStatus === "observed");
  return observed?.gmrComment ?? observed?.lineComment ?? null;
}
